import type { ThemeObject } from "@circle-react/types/Theme";
import { useEffect, useMemo } from "react";
import { useFrame } from "react-frame-component";

import { APPEARANCE } from "../themes";
import { isAppearance } from "../themes";
import { updateTheme } from "../updateTheme";
import { useLocalStorageAppearance } from "./useLocalStorageAppearance";

export const useThemeLocalStorage = (
  {
    default_appearance = APPEARANCE.LIGHT,
    custom_colors = {},
    current_theme = "default",
    is_custom = false,
    brand_color = {
      light: "",
      dark: "",
    },
    brand_text_color = {
      light: "",
      dark: "",
    },
  }: ThemeObject,
  selector = "",
) => {
  const { document } = useFrame();
  const { documentElement } = document ?? {};
  const {
    currentAppearance,
    setAppearance,
    clearAppearance,
    storedPreference,
    toggleAppearance,
    isDarkAppearance,
    isLightAppearance,
  } = useLocalStorageAppearance({ defaultAppearance: default_appearance });

  // Not simply `currentAppearance ?? default_appearance`: currentAppearance is ""
  // whenever there's no explicit stored preference (even "system" resolves to a
  // concrete value upstream only in that one case), which would silently bypass
  // useLocalStorageAppearance's own resolution priority (explicit > system > mobile
  // webview > community default) and re-derive a cruder fallback here instead.
  // isDarkAppearance already reflects that full resolution, so derive from it.
  const appearance = isAppearance(currentAppearance)
    ? currentAppearance
    : isDarkAppearance
      ? APPEARANCE.DARK
      : APPEARANCE.LIGHT;

  useEffect(() => {
    updateTheme({
      theme: {
        current_appearance: appearance,
        custom_colors,
        current_theme,
        is_custom,
        brand_color,
        brand_text_color,
      },
      documentElement: documentElement,
      selector,
    });
  }, [
    brand_color,
    brand_text_color,
    appearance,
    current_theme,
    custom_colors,
    documentElement,
    is_custom,
    selector,
  ]);

  return useMemo(
    () => ({
      currentTheme: current_theme,
      currentAppearance: appearance,
      isDarkAppearance,
      isLightAppearance,
      isCustomTheme: is_custom,
      customColors: custom_colors,
      brandColor: brand_color,
      brandTextColor: brand_text_color,
      toggleAppearance,
      setAppearance,
      clearAppearance,
      storedPreference,
    }),
    [
      current_theme,
      appearance,
      isDarkAppearance,
      isLightAppearance,
      is_custom,
      custom_colors,
      brand_color,
      brand_text_color,
      toggleAppearance,
      setAppearance,
      clearAppearance,
      storedPreference,
    ],
  );
};
